import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridStackOptions } from 'gridstack';
import { elementCB, nodesCB } from 'gridstack/dist/angular';
import { ContextMenuModule } from 'primeng/contextmenu';
import { ContextMenu } from 'primeng/contextmenu';
import { DashboardPanelLayoutModel, DashboardWorkspaceModel, UiComponentModel } from '../models/dashboard.model';
import { forkJoin, lastValueFrom, Subject, Subscription } from 'rxjs';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { CompactModeChangedMessage,
  DataSavedMessage, 
  GridItemResizedMessage, 
  OpenSearchComponentDropdown, 
  PanelModeChangedMessage, 
  SetPanelColumnsMessage, 
  ToggleCompactModeMessage, 
  TogglePanelModeMessage, 
  ToggleWellSchematicSidebarMessage, 
  ToggleWizardModeMessage, 
  WizardModeChangedMessage, 
  WizardStepChangedMessage, 
  WizardStepNavigateMessage, 
  WorkspaceCloned } from 'src/app/shared/models/mediator-messages.model';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { UserRoles } from '../../user-admin-page/user-model';
import { ConfirmationService, MenuItem, PrimeIcons } from 'primeng/api';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { DialogService } from 'primeng/dynamicdialog';
import { UnitsConvertComponent } from '../../units-convert/units-convert.component';
import { Router } from '@angular/router';
import { DashboardApiService } from '../services/dashboard-api.service';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { UserUnitsModel } from '../../user-units/user-units.model';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { ComponentLauncher } from '../services/component-launcher';
import { WorkspaceGridstackComponent } from './workspace-gridstack/workspace-gridstack.component';
import { WorkspacePanelComponent } from './workspace-panel/workspace-panel.component';
import { WORKSPACE_PANEL_WIZARD_STEPS } from './workspace-panel/workspace-panel-wizard.model';
import { StableCompactGridStackEngine } from './workspace-gridstack/stable-compact-gridstack.engine';

/*
  Container component for the Grid Workspace. Owns state and business logic;
  delegates rendering to WorkspaceGridstackComponent and WorkspacePanelComponent.
*/
@Component({
    selector: 'app-workspace',
    templateUrl: './workspace-component.html',
    styles: [`:host { display: block; flex: 1; min-height: 0; }`],
    standalone: true,
    imports: [CommonModule, ContextMenuModule, WorkspaceGridstackComponent, WorkspacePanelComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardWorkspaceComponent implements OnInit, OnChanges, OnDestroy {

  private readonly _destroy$ = new Subject<void>();
  private readonly _uiComponentUpdatedDebouncer = new Subject<UiComponentModel[]>();
  private readonly _subscriptions = new Subscription();
  private _organization!: string;
  private _diameterLabel!: string;
  private _signalRCallback!: (data: any) => void;
  private readonly _fullscreenChangeHandler = (): void => this.updateContextMenuItems();
  private _loadedDesignId: string | null = null;
  private _panelModeAutoEnabledForWizard = false;

  public readonly stringDependentComponents = ['Annulus Contents', 'Cementing & Landing', 'Drill String', 'Drill String 2', 'Wellhead', 'Packers', 'Risers', 'Design Factors',
    'Pre-Install Load Cases', 'Post-Install Load Cases', 'Load P&T Profiles', 'Design Limits Plot', 'Safety Factor Results', 'Connection Safety Factor Results', 'Packer Envelope', 'Packer Results Table',
    'Press & Temp', 'Barlow Burst Results', 'API Collapse Results', 'Axial Forces Results', 'Forces/Displacements', 'Buckling Summary', 'Lift-Off Results', 'Max Allowed Wear', 'Thermal Operations',
    'Thermal Results - Temperatures', 'Thermal Results - Pressures', 'Annular Results', 'Operation Envelope', 'Packer Schematic', 'Wellhead Movement Inputs'
  ];

  public readonly gridOptions: GridStackOptions = {
    margin: 3,
    column: 50,
    // 'initial' computes cell height once at init (same as 'auto') but does NOT set up a
    // ResizeObserver, so scrollbar appearance never triggers a recalculation feedback loop.
    // A window resize listener in WorkspaceGridstackComponent keeps cells square on true resizes.
    cellHeight: 'initial',
    float: false,
    engineClass: StableCompactGridStackEngine,
    resizable: { handles: 'e, se, s' },
    draggable: { handle: '.drag-handler' },
  };

  // Signal-based state — writes here trigger Angular's scheduler even from async/zone callbacks
  public readonly userRoles = signal<UserRoles | null>(null);
  public readonly panelModeLoaded = signal(false);
  public readonly panelMode = signal(false);
  public readonly compactMode = signal(true);
  public readonly compactRequestVersion = signal(0);
  public readonly currentStringName = signal('');
  public readonly holeSectionName = signal('');
  public readonly componentInstance = signal('');
  public readonly panelColumnCount = signal(2);
  public readonly panelRowSizes = signal<number[]>([]);
  public readonly panelColSizes = signal<number[]>([]);
  public readonly panelWizardMode = signal<boolean>(false);
  public readonly panelWizardStepIndex = signal<number>(0);
  public readonly contextMenuItems = signal<MenuItem[]>([]);

  public availableComponents: { key: string; name: string }[] = [];

  @ViewChild('workspaceCm') workspaceCm!: ContextMenu;

  @Input() public workspace!: DashboardWorkspaceModel;
  @Input() public scrollToItemId: string | null = null;

  @Output() public uiComponentsRemoved = new EventEmitter<UiComponentModel[]>();
  @Output() public uiComponentXyUpdated = new EventEmitter<UiComponentModel[]>();
  @Output() public workspaceRefreshed = new EventEmitter<void>();

  constructor(
    private _messenger: MediatorService,
    private _storeService: StoreService,
    private _dialogService: DialogService,
    private _router: Router,
    private _dashboardService: DashboardApiService,
    private _confirmationService: ConfirmationService,
    private _wellConfigService: WellConfigService,
    private _signalRService: SignalRService,
    private _componentLauncher: ComponentLauncher,
    private _cdr: ChangeDetectorRef
  ) {
    this._subscriptions.add(this._messenger.of(DataSavedMessage).subscribe((message: DataSavedMessage) => {
      this.componentInstance.set(message.componentInstance);
      setTimeout(() => this.componentInstance.set(''), 1500);
    }));
  }

  async ngOnInit(): Promise<void> {
    this.userRoles.set(await this._storeService.get<UserRoles>(StorageKeys.ROLES));
    const user = await this._storeService.get<any>(StorageKeys.USER);
    this._organization = user?.profile?.organization || null;

    const units = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this._diameterLabel = units?.shortLengths === 'in' ? '"' : (units?.shortLengths ?? '');
    this.getStringLabel();

    const registry = this._componentLauncher.getComponentRegistry();
    this.availableComponents = registry
      .filter((c: any) => c.key !== 'sectionsPanel')
      .map((c: any) => ({ key: c.key, name: c.name }));

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRCallback = (data: any) => {
      const appIds = this._storeService.getSession<any>(SessionStorageKeys.APP_IDS);
      const activeDesignId = appIds?.designId;
      if (!data?.designId || !activeDesignId || data.designId === activeDesignId) {
        this.signalRfunc(data);
      }
    };
    hub.on(SignalRService.ON_PFB_CHANGE, this._signalRCallback);

    this._uiComponentUpdatedDebouncer
      .pipe(debounceTime(500), takeUntil(this._destroy$))
      .subscribe(value => this.uiComponentXyUpdated.emit(value));

    this.updateContextMenuItems();
    document.addEventListener('fullscreenchange', this._fullscreenChangeHandler);

    this._subscriptions.add(
      this._messenger.of(TogglePanelModeMessage).subscribe(() => this.togglePanelMode())
    );

    this._subscriptions.add(
      this._messenger.of(ToggleWizardModeMessage).subscribe(() => this.togglePanelWizardMode())
    );

    this._subscriptions.add(
      this._messenger.of(WizardStepNavigateMessage).subscribe(msg => this.navigatePanelWizardStep(msg.direction))
    );

    this._subscriptions.add(
      this._messenger.of(ToggleCompactModeMessage).subscribe(() => this.toggleCompactMode())
    );

    this._subscriptions.add(
      this._messenger.of(SetPanelColumnsMessage).subscribe(async (msg: SetPanelColumnsMessage) => {
        this.panelColumnCount.set(msg.columns);
        this.panelRowSizes.set([]);
        this.panelColSizes.set([]);
        if (!this.panelMode()) {
          await this.togglePanelMode();
        } else {
          await this.savePanelLayout();
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const wsChange = changes.workspace;
    const currentDesignId = this._storeService.getSession<any>(SessionStorageKeys.APP_IDS)?.designId ?? null;
    const designChanged = currentDesignId !== this._loadedDesignId;
    const shouldReloadPanelMode = wsChange?.currentValue &&
      (!wsChange.previousValue || wsChange.previousValue.index !== wsChange.currentValue.index || designChanged);

    if (shouldReloadPanelMode) {
      this._loadedDesignId = currentDesignId;
      // Hide workspace content immediately so grid state from the previous tab cannot leak into
      // the new tab while persisted per-workspace settings are being loaded.
      this.panelModeLoaded.set(false);
      this.reloadWorkspaceLayoutState();
    }
    this.getStringLabel();
  }

  private reloadWorkspaceLayoutState(): void {
    // Defer async store reads to the next microtask to avoid mutating signals in the same
    // change-detection pass that triggered ngOnChanges.
    queueMicrotask(() => {
      this.loadPanelModeAsync()
        .then(() => {
          this.panelModeLoaded.set(true);
          this._cdr.markForCheck();
        })
        .catch((err) => {
          console.error('[workspace-component] loadPanelModeAsync FAILED:', err);
          this.panelModeLoaded.set(true);
          this._cdr.markForCheck();
        });
    });
  }

  signalRfunc(data: any): void {
    if (data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.getStringLabel().catch(() => {
        this.currentStringName.set('');
        this.holeSectionName.set('');
      });
    }
  }

  onContextMenu(event: MouseEvent): void {
    if (event instanceof PointerEvent && event.pointerType === 'touch') return;
    event.preventDefault();
    this.workspaceCm.show(event);
  }

  private compactModeKey(): string {
    const designId = this._storeService.getSession<any>(SessionStorageKeys.APP_IDS)?.designId ?? 'unknown';
    return `compactMode@${designId}`;
  }

  private legacyPanelStateKey(name: string): string {
    const designId = this._storeService.getSession<any>(SessionStorageKeys.APP_IDS)?.designId ?? 'unknown';
    return `${name}@${designId}@${this.workspace.index}`;
  }

  private async loadPanelModeAsync(): Promise<void> {
    if (!this.workspace) {
      return;
    }
    const panelLayout = this.workspace.panelLayout;
    const migratedPanelLayout = panelLayout ?? await this.loadLegacyPanelLayout();
    this.panelMode.set(migratedPanelLayout?.isEnabled ?? false);
    this.compactMode.set((await this._storeService.get<boolean>(this.compactModeKey())) ?? true);
    this.gridOptions.float = !this.compactMode();
    this.panelColumnCount.set(migratedPanelLayout?.columnCount ?? 2);
    this.panelRowSizes.set(migratedPanelLayout?.rowSizes ?? []);
    this.panelColSizes.set(migratedPanelLayout?.columnSizes ?? []);
    this.panelWizardMode.set(migratedPanelLayout?.wizardMode ?? false);
    this.panelWizardStepIndex.set(migratedPanelLayout?.wizardStepIndex ?? 0);
    if (!panelLayout && migratedPanelLayout) {
      this.workspace.panelLayout = migratedPanelLayout;
      await this.savePanelLayout();
    }
    this.publishPanelModeChanged();
    this.publishWizardModeChanged();
    this.publishCompactModeChanged();
    this.updateContextMenuItems();
  }

  private async loadLegacyPanelLayout(): Promise<DashboardPanelLayoutModel | undefined> {
    const [isEnabled, columnCount, rowSizes, columnSizes, wizardMode, wizardStepIndex] = await Promise.all([
      this._storeService.get<boolean>(this.legacyPanelStateKey('panelMode')),
      this._storeService.get<number>(this.legacyPanelStateKey('panelCols')),
      this._storeService.get<number[]>(this.legacyPanelStateKey('panelRowSizes')),
      this._storeService.get<number[]>(this.legacyPanelStateKey('panelColSizes')),
      this._storeService.get<boolean>(this.legacyPanelStateKey('panelWizardMode')),
      this._storeService.get<number>(this.legacyPanelStateKey('panelWizardStepIndex'))
    ]);

    if (isEnabled === null && columnCount === null && rowSizes === null && columnSizes === null && wizardMode === null && wizardStepIndex === null) {
      return undefined;
    }

    return new DashboardPanelLayoutModel({
      isEnabled: isEnabled ?? false,
      columnCount: columnCount ?? 2,
      rowSizes: rowSizes ?? [],
      columnSizes: columnSizes ?? [],
      wizardMode: wizardMode ?? false,
      wizardStepIndex: wizardStepIndex ?? 0
    });
  }

  private publishPanelModeChanged(): void {
    this._messenger.publish(new PanelModeChangedMessage(this.panelMode()));
  }

  private publishWizardModeChanged(): void {
    this._messenger.publish(new WizardModeChangedMessage(this.panelWizardMode()));
    this.publishWizardStepChanged();
  }

  private publishWizardStepChanged(): void {
    const idx = this.panelWizardStepIndex();
    const step = WORKSPACE_PANEL_WIZARD_STEPS[idx];
    this._messenger.publish(new WizardStepChangedMessage(
      idx,
      WORKSPACE_PANEL_WIZARD_STEPS.length,
      step?.label ?? '',
      WORKSPACE_PANEL_WIZARD_STEPS.map(s => s.label)
    ));
  }

  private compactWorkspace(): void {
    this.compactRequestVersion.update(v => v + 1);
  }

  private async toggleCompactMode(): Promise<void> {
    const nextCompactMode = !this.compactMode();
    this.compactMode.set(nextCompactMode);
    this.gridOptions.float = !nextCompactMode;
    await this._storeService.set(this.compactModeKey(), nextCompactMode);
    this.publishCompactModeChanged();
    this.updateContextMenuItems();
    if (nextCompactMode) {
      this.compactWorkspace();
    }
  }

  private publishCompactModeChanged(): void {
    this._messenger.publish(new CompactModeChangedMessage(this.compactMode()));
  }

  private updateContextMenuItems(): void {
    const items: MenuItem[] = [
      { label: 'Clear Workspace', icon: PrimeIcons.TIMES, command: () => this.removeAllComponents() },
      { label: 'Component Search', icon: PrimeIcons.SEARCH, command: () => this._messenger.publish(new OpenSearchComponentDropdown()) },
      { label: 'Toggle Schematic', icon: PrimeIcons.ARROW_RIGHT_ARROW_LEFT, command: () => this.toggleSchematic() },
      { label: 'Units Converter', icon: PrimeIcons.CALCULATOR, command: () => this.openUnitsConverter() },
      { label: 'Units', icon: PrimeIcons.SERVER, command: () => this._router.navigate(['/profile/units']) },
      { label: 'Apply default workspace', icon: PrimeIcons.REFRESH, command: () => this.cloneDashboard() },
      { label: 'Refresh workspace', icon: PrimeIcons.SYNC, command: () =>  this.workspaceRefreshed.emit() },
      { label: 'Toggle Panel Mode', icon: PrimeIcons.OBJECTS_COLUMN, command: () => this.togglePanelMode() },
      {
        label: this.panelWizardMode() ? 'Exit Wizard Mode' : 'Enter Wizard Mode',
        icon: PrimeIcons.BOLT,
        command: () => this.togglePanelWizardMode()
      },
      { label: document.fullscreenElement ? 'Exit Full Screen' : 'Full Screen', icon: PrimeIcons.EXPAND, command: () => this.toggleFullScreen() }
    ];

    if (!this.panelMode()) {
      items.splice(7, 0, {
        label: this.compactMode() ? 'Compact Mode: ON' : 'Compact Mode: OFF',
        icon: PrimeIcons.LIST_CHECK,
        command: () => this.toggleCompactMode()
      });
    }

    this.contextMenuItems.set(items);
  }

  private async toggleFullScreen(): Promise<void> {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await document.documentElement.requestFullscreen();
    }
  }

  public async togglePanelMode(): Promise<void> {
    this.panelMode.set(!this.panelMode());
    await this.savePanelLayout();
    if (this.panelWizardMode()) {
      await this.onPanelWizardModeChange(false);
    }
    this.publishPanelModeChanged();
    this.updateContextMenuItems();
  }

  public async togglePanelWizardMode(): Promise<void> {
    const enteringWizard = !this.panelWizardMode();
    if (enteringWizard) {
      if (!this.panelMode()) {
        await this.togglePanelMode();
        this._panelModeAutoEnabledForWizard = true;
      } else {
        this._panelModeAutoEnabledForWizard = false;
      }
      await this.onPanelWizardModeChange(true);
    } else {
      await this.onPanelWizardModeChange(false);
      if (this._panelModeAutoEnabledForWizard && this.panelMode()) {
        await this.togglePanelMode();
      }
      this._panelModeAutoEnabledForWizard = false;
    }
  }

  public async navigatePanelWizardStep(direction: 'prev' | 'next' | number): Promise<void> {
    if (!this.panelWizardMode()) return;
    const total = WORKSPACE_PANEL_WIZARD_STEPS.length;
    const current = this.panelWizardStepIndex();
    let next: number;
    if (typeof direction === 'number') {
      next = direction;
    } else {
      next = direction === 'prev' ? current - 1 : current + 1;
    }
    if (next < 0 || next >= total || next === current) return;
    await this.onPanelWizardStepChange(next);
  }

  private async getStringLabel(): Promise<void> {
    try {
      const { tubular, tubulars } = await lastValueFrom(forkJoin({
        tubular: this._wellConfigService.getTubular(),
        tubulars: this._wellConfigService.getStringsList()
      })) as any;
      if (!tubular || !tubular?.stringSections?.[0]?.pipe) {
        this.currentStringName.set('');
        this.holeSectionName.set('');
        return;
      }
      const tubularName = tubulars.find(t => t.id === tubular.id)?.name || '';
      const decimalFactor = this._diameterLabel === '"' ? 1000 : 100;
      this.currentStringName.set(` - ${this.formatTubularName(tubularName, decimalFactor)}`);
      this.holeSectionName.set(tubular.type != 'Tubing' && tubular.type != 'Tieback' ? ` - ${this.formatDecimal(tubular.holeSize, decimalFactor)}${this._diameterLabel} Hole Section` : '');
    } catch {
      this.currentStringName.set('');
      this.holeSectionName.set('');
    }
  }

  private formatTubularName(name: string, decimalFactor: number): string {
    return name.replace(/\d+\.\d+/g, value =>
      String(Math.round(Number(value) * decimalFactor) / decimalFactor)
    );
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : '';
  }

  // ── Grid event handlers (forwarded from WorkspaceGridstackComponent) ──────────

  public onGridChanged(data: nodesCB): void {
    this._uiComponentUpdatedDebouncer.next(this.workspace.uiComponents);
  }

  public onGridItemAdded(data: nodesCB): void {
    // addedCB fires after GridStack has computed and applied inline dimensions to each element,
    // so el.clientHeight is the actual rendered pixel height — reliable for setting tableHeight.
    data.nodes.forEach(node => {
      const item = this.workspace.uiComponents.find(c => c.id === node.id);
      const el = node.el as HTMLElement | undefined;
      if (item && el && el.clientHeight > 0) {
        this._messenger.publish(new GridItemResizedMessage(item.name, el.clientHeight, el.clientWidth));
      }
    });
  }

  public onGridItemResized(data: elementCB): void {
    const id = data.el.gridstackNode?.id;
    const item = this.workspace.uiComponents.find(c => c.id === id);
    if (item) {
      this._messenger.publish(new GridItemResizedMessage(item.name, data.el.clientHeight, data.el.clientWidth));
    }
  }

  // ── Panel event handlers (forwarded from WorkspacePanelComponent) ─────────────

  public async onPanelRowResizeEnd(sizes: number[]): Promise<void> {
    this.panelRowSizes.set(sizes);
    await this.savePanelLayout();
  }

  public async onPanelColResizeEnd(sizes: number[]): Promise<void> {
    this.panelColSizes.set(sizes);
    await this.savePanelLayout();
  }

  public async onPanelWizardModeChange(enabled: boolean): Promise<void> {
    this.panelWizardMode.set(enabled);
    await this.savePanelLayout();
    this.publishWizardModeChanged();
    this.updateContextMenuItems();
  }

  public async onPanelWizardStepChange(index: number): Promise<void> {
    this.panelWizardStepIndex.set(index);
    await this.savePanelLayout();
    this.publishWizardStepChanged();
  }

  private async savePanelLayout(): Promise<void> {
    const panelLayout = new DashboardPanelLayoutModel({
      isEnabled: this.panelMode(),
      columnCount: this.panelColumnCount(),
      rowSizes: this.panelRowSizes(),
      columnSizes: this.panelColSizes(),
      wizardMode: this.panelWizardMode(),
      wizardStepIndex: this.panelWizardStepIndex()
    });
    this.workspace.panelLayout = panelLayout;
    await lastValueFrom(this._dashboardService.updateUiComponents(this.workspace.uiComponents, panelLayout));
  }

  // ── Component removal ─────────────────────────────────────────────────────────

  public removeUiComponent(item: UiComponentModel): void {
    this.removeUiComponents([item]);
  }

  public async removeUiComponents(uiComponents: UiComponentModel[]): Promise<void> {
    for (const uiComponent of uiComponents) {
      await this._storeService.remove(uiComponent.id);
    }
    this.uiComponentsRemoved.emit(uiComponents);
    setTimeout(() => this._messenger.publish(new GridItemResizedMessage('none', 0, 0)), 100);
  }

  private async removeAllComponents(): Promise<void> {
    const uiComponents = [...this.workspace.uiComponents];
    await this.removeUiComponents(uiComponents);
    this.workspace.uiComponents = [];
  }

  // ── Utility actions ───────────────────────────────────────────────────────────

  private cloneDashboard(): void {
    this._confirmationService.confirm({
      message: 'This will replace your current workspace layout with the default one. Do you want to proceed?',
      header: 'Confirm',
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      accept: () => {
        this._dashboardService.cloneDashboard('00000000-0000-0000-0000-000000000000', this._organization).subscribe(() => {
          this._messenger.publish(new WorkspaceCloned());
        });
      }
    });
  }

  private toggleSchematic(): void {
    this._messenger.publish(new ToggleWellSchematicSidebarMessage());
    this._messenger.publish(new GridItemResizedMessage('none', 0, 0));
  }

  private openUnitsConverter(): void {
    this._dialogService.open(UnitsConvertComponent, {
      header: 'Units Converter',
      width: '400px',
      closable: true,
      draggable: true,
      modal: false,
      styleClass: 'popup-dialog',
      style: { position: 'absolute', top: '75px', right: '20px' }
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this._fullscreenChangeHandler);
    this._destroy$.next();
    this._destroy$.complete();
    this._subscriptions.unsubscribe();
    const hub = this._signalRService.getConnectionToNotificationHub();
    if (this._signalRCallback) hub.off(SignalRService.ON_PFB_CHANGE, this._signalRCallback);
  }
}
